import { useEffect, useRef, type ReactNode } from 'react';
import { CheckIcon, CloseIcon } from './icons.tsx';
import s from './Sheet.module.css';

interface SheetProps {
  label: string;
  onClose: () => void;
  children: ReactNode;
  /** full: player (10 px from top); tall: profile (48 px); auto: content height (paywall). */
  height?: 'auto' | 'full' | 'tall';
  /** Grouped background (--bg) for inset-grouped content like the profile. */
  grouped?: boolean;
  title?: string;
  closeLabel?: string;
}

const DRAG_CLOSE_PX = 90;

/**
 * Bottom sheet: grabber, optional title + close, backdrop tap and swipe-down to close.
 * The Telegram BackButton is wired globally (App) to pop the top layer.
 */
export function Sheet({
  label,
  onClose,
  children,
  height = 'auto',
  grouped,
  title,
  closeLabel,
}: SheetProps) {
  const ref = useRef<HTMLElement>(null);
  const drag = useRef<{ y: number; dy: number } | null>(null);

  useEffect(() => {
    ref.current?.focus({ preventScroll: true });
  }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    const el = ref.current;
    // Only start a drag from the top area or when the content is scrolled to the top.
    if (!el || el.scrollTop > 0) return;
    drag.current = { y: e.clientY, dy: 0 };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const el = ref.current;
    if (!drag.current || !el) return;
    drag.current.dy = Math.max(0, e.clientY - drag.current.y);
    el.style.transform = `translateY(${drag.current.dy}px)`;
    el.style.transition = 'none';
  };
  const onPointerUp = () => {
    const el = ref.current;
    if (!drag.current || !el) return;
    const closed = drag.current.dy > DRAG_CLOSE_PX;
    drag.current = null;
    el.style.transition = 'transform 260ms var(--spring-sheet)';
    el.style.transform = '';
    if (closed) onClose();
  };

  return (
    <>
      <div className={s.backdrop} onClick={onClose} aria-hidden="true" />
      <section
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        className={[
          s.sheet,
          grouped && s.grouped,
          height === 'full' && s.full,
          height === 'tall' && s.tall,
        ]
          .filter(Boolean)
          .join(' ')}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <div className={s.grabber} aria-hidden="true" />
        {title !== undefined && (
          <div className={s.header}>
            <span className={s.spacer} />
            <h1 className={s.title}>{title}</h1>
            <button type="button" className={s.close} aria-label={closeLabel} onClick={onClose}>
              <span className={s.closeDot}>
                <CloseIcon />
              </span>
            </button>
          </div>
        )}
        {children}
      </section>
    </>
  );
}

/** Action sheet with options + "Done" (design/SleepTimer.dc.html). */
export function ActionSheet<V extends string>({
  label,
  title,
  hint,
  options,
  value,
  onSelect,
  doneLabel,
  onClose,
}: {
  label: string;
  title: string;
  hint?: string;
  options: { value: V; label: string }[];
  value: V;
  onSelect: (v: V) => void;
  doneLabel: string;
  onClose: () => void;
}) {
  return (
    <>
      <div className={`${s.backdrop} ${s.actionBackdrop}`} onClick={onClose} aria-hidden="true" />
      <div role="dialog" aria-modal="true" aria-label={label} className={s.action}>
        <div className={s.actionCard}>
          <div className={s.actionHead}>
            <span className="t-headline" style={{ fontWeight: 800 }}>
              {title}
            </span>
            {hint && <span className="t-footnote secondary">{hint}</span>}
          </div>
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              className={s.actionOption}
              aria-pressed={o.value === value}
              onClick={() => {
                onSelect(o.value);
              }}
            >
              <span>{o.label}</span>
              {o.value === value && <CheckIcon size={20} width={2.6} />}
            </button>
          ))}
        </div>
        <button type="button" className={s.actionDone} onClick={onClose}>
          {doneLabel}
        </button>
      </div>
    </>
  );
}

/** Action menu (player "…"): a list of actions + Cancel, in the same visual as ActionSheet. */
export function MenuSheet({
  label,
  items,
  cancelLabel,
  onClose,
}: {
  label: string;
  items: { label: string; value?: string; onSelect: () => void }[];
  cancelLabel: string;
  onClose: () => void;
}) {
  return (
    <>
      <div className={`${s.backdrop} ${s.actionBackdrop}`} onClick={onClose} aria-hidden="true" />
      <div role="dialog" aria-modal="true" aria-label={label} className={s.action}>
        <div className={s.actionCard} role="menu">
          {items.map((item, i) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              className={s.actionOption}
              style={i === 0 ? { borderTop: 0 } : undefined}
              onClick={item.onSelect}
            >
              <span>{item.label}</span>
              {item.value && <span className="secondary">{item.value}</span>}
            </button>
          ))}
        </div>
        <button type="button" className={s.actionDone} onClick={onClose}>
          {cancelLabel}
        </button>
      </div>
    </>
  );
}
